/**
 * `<input type="datetime-local">` has no offset in its value and is interpreted
 * in the browser's timezone, while everything on the wire is UTC — so both
 * directions have to be spelled out rather than left to `toISOString` or the
 * `Date` constructor guessing.
 */
export function toLocalInput(iso: string | null): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`
}

export function fromLocalInput(value: string): string | null {
  if (!value) return null
  // A datetime-local string carries no offset, so this parses as local time —
  // which is exactly what the operator typed.
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date.toISOString()
}

/**
 * The UTC equivalent, shown beside every window field. Without it an operator
 * in GMT+8 sets "09:00", the thing goes live at 01:00 their time, and nobody
 * can explain why.
 *
 * Still UTC on purpose, and more useful now that everything else reads local:
 * it is the shared reference when two people in different countries are talking
 * about the same scheduled window, and when someone matches a window against a
 * backend log.
 */
export function utcLabel(iso: string | null): string {
  return iso ? `${iso.slice(0, 16).replace('T', ' ')} UTC` : ''
}

/**
 * A past timestamp for reading, in the viewer's own timezone.
 *
 * The zone is always spelled out. An unlabelled "17:00" is the worst of the
 * options — the operator cannot tell whose clock it is, and this console is read
 * from more than one country. Storage and the wire stay UTC: every API timestamp
 * is RFC3339 with a Z, which is what lets `new Date` resolve it correctly rather
 * than guessing. This is presentation only.
 *
 * Assembled from parts rather than `toLocaleString` so the order stays
 * year-month-day whatever locale the browser reports. These sit in table
 * columns, and a US-ordered date in one row next to an ISO-ordered one in the
 * next is unscannable.
 */
/**
 * The instant a local calendar day begins, as a UTC RFC3339 string, and the
 * instant the following day begins — a half-open `[from, to)` pair.
 *
 * `<input type="date">` yields the operator's *local* calendar date, while the
 * API filters on UTC instants. Converting here is what makes "August 12" mean
 * the operator's August 12. Without it a Singapore operator filtering for the
 * 12th silently loses everything that happened before 08:00 that morning, since
 * those rows are still the 11th in UTC — and they can see those rows in the
 * table, dated the 12th, which is what makes the bug so confusing.
 *
 * The server deliberately does none of this: only the client knows the
 * operator's zone, so a server handed a bare date would have to guess, and any
 * guess is wrong for somebody.
 */
export function startOfLocalDayUtc(yyyyMmDd: string): string | null {
  return localDayBoundaryUtc(yyyyMmDd, 0)
}

/** The exclusive upper bound: the instant the day after `yyyyMmDd` begins. */
export function endOfLocalDayUtc(yyyyMmDd: string): string | null {
  return localDayBoundaryUtc(yyyyMmDd, 1)
}

function localDayBoundaryUtc(yyyyMmDd: string, addDays: number): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(yyyyMmDd)
  if (!match) return null

  const [, year, month, day] = match
  // Month is 0-based here. Day overflow normalises — 32 August becomes
  // 1 September — which is exactly what the exclusive upper bound needs at a
  // month or year boundary, so no special-casing.
  const date = new Date(Number(year), Number(month) - 1, Number(day) + addDays)
  return Number.isNaN(date.getTime()) ? null : date.toISOString()
}

/**
 * The calendar date in the viewer's timezone, for columns that show a date
 * rather than a moment.
 *
 * These used to be `iso.slice(0, 10)`, which reads the UTC date straight out of
 * the string. That is a day out for anyone east of Greenwich for part of every
 * day: an action taken at 09:00 in Singapore is 01:00 UTC and lists correctly,
 * but one taken at 07:00 is 23:00 UTC the day before, and appeared under
 * yesterday's date with nothing to explain it.
 *
 * No zone suffix here, unlike localStamp — a bare date carries far less
 * ambiguity than a bare time, and these sit in narrow table columns.
 */
export function localDate(iso: string | null): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''

  const parts = new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date)

  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? ''

  return `${part('year')}-${part('month')}-${part('day')}`
}

export function localStamp(iso: string | null): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''

  const parts = new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    // h23 rather than hour12:false — the latter renders midnight as "24:00" in
    // some engines.
    hourCycle: 'h23',
    timeZoneName: 'shortOffset',
  }).formatToParts(date)

  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? ''

  return `${part('year')}-${part('month')}-${part('day')} ${part('hour')}:${part('minute')} ${part('timeZoneName')}`
}
